import { accountsSelectors, accountsUtils } from 'app-components/flux/accounts';
import { selectIsAiBusinessToolsEnabled } from 'app-components/flux/ai-chat/aiChatSelectors';
import { businessSelectors } from 'app-components/flux/business';
import { getCategoriesById } from 'app-components/flux/categories/categoriesSelectors';
import { getResourcesById as getTransactionsById } from 'app-components/flux/transactions/transactionsSelectors';
import type { TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import type RootState from 'app-components/utils/redux-store/rootState';

export type TransactionBusinessFields = {
  businessId?: string | null;
  customerId?: string | null;
  projectId?: string | null;
  isBillable?: boolean | null;
};

export const resolveBusinessFieldsForCategory = (
  state: RootState,
  {
    categoryId,
    accountId,
    current,
  }: { categoryId: string | undefined; accountId: string | null | undefined; current: TransactionBusinessFields },
): TransactionBusinessFields => {
  if (!categoryId || !selectIsAiBusinessToolsEnabled(state)) return {};

  const category = getCategoriesById(state).get(categoryId);
  if (!category) return {};

  const hasBusiness = !!accountsUtils.normalizeBusinessIdForUsage(current.businessId);

  if (category.usageType === 'BUSINESS') {
    if (hasBusiness) return {};
    const accountBusinessId = accountId
      ? accountsUtils.normalizeBusinessIdForUsage(accountsSelectors.getAccountsById(state).get(accountId)?.businessId)
      : undefined;
    const businessId = accountBusinessId ?? businessSelectors.getDefaultBusiness(state)?.id;
    return businessId ? { businessId } : {};
  }

  if (hasBusiness) {
    return { businessId: undefined, customerId: '', projectId: '', isBillable: false };
  }

  return {};
};

const categoryIdOf = (txn: TransactionRecord): string | undefined =>
  txn.coa?.type === 'CATEGORY' ? txn.coa.id : undefined;

export const reconcileReviewedBusinessFields = (state: RootState, reviewed: TransactionRecord): TransactionRecord => {
  const original = reviewed.id ? getTransactionsById(state).get(reviewed.id) : undefined;
  if (!original) return reviewed;

  const restored = reviewed.merge({
    businessId: original.businessId,
    customerId: original.customerId,
    projectId: original.projectId,
    isBillable: original.isBillable,
  });

  const finalCategoryId = categoryIdOf(reviewed);
  if (finalCategoryId === categoryIdOf(original)) return restored;

  return restored.merge(
    resolveBusinessFieldsForCategory(state, {
      categoryId: finalCategoryId,
      accountId: original.accountId,
      current: original,
    }),
  );
};
